import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { startCheckout } from "@/lib/checkout";

const DETAILS = {
  "Resume Refresh": "ATS-friendly formatting, professional editing, and targeted improvements to make your existing resume sharper and more competitive.",
  "Complete Resume Revamp": "A comprehensive resume rewrite focused on accomplishments, positioning, and your career goals — built to tell a compelling story of impact.",
  "Resume + LinkedIn Bundle": "A complete resume revamp paired with full LinkedIn optimization — headline, About section, experience, and personal branding working together.",
  "Cover Letter": "Personalized, position-specific cover letter writing that complements your resume and speaks directly to the role you want.",
  "Interview Coaching": "Virtual interview coaching, practice questions, and personalized feedback to help you walk in prepared and confident.",
  "Career Strategy Consultation": "One-on-one guidance for career transitions, job search strategy, and professional positioning — a clear plan for your next move.",
};

function priceLabel(p) {
  if (p.checkout_enabled && p.price_amount) {
    return `$${(p.price_amount / 100).toFixed(0)}${p.is_draft ? " (draft)" : ""}`;
  }
  return p.price_display || "Inquire";
}

export default function ForProfessionals() {
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", service: "", target_role: "", career_goals: "", current_situation: "", document_ids: [], hp: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const loadPackages = useCallback(() => {
    api.get("/packages", { params: { audience: "professional" } }).then((r) => setPackages(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadPackages(); }, [loadPackages]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-ivory border border-champagne/40 rounded-lg px-4 py-3 text-espresso focus:outline-none focus:ring-2 focus:ring-champagne focus:border-transparent placeholder:text-espresso/40";

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((f) => [...f, { id: data.id, name: data.filename }]);
      setForm((frm) => ({ ...frm, document_ids: [...frm.document_ids, data.id] }));
      toast.success("Document uploaded securely.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (id) => {
    setFiles((f) => f.filter((x) => x.id !== id));
    setForm((frm) => ({ ...frm, document_ids: frm.document_ids.filter((x) => x !== id) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/intakes", form);
      setDone(true);
      toast.success("Intake received. We'll reach out to get started.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="for-professionals-page">
      <SEO title="For Professionals" description="Resume writing, LinkedIn optimization, cover letters, interview coaching, and career strategy — boutique career services to elevate your career." />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">The Career Atelier</span>
            <h1 className="font-serif text-5xl md:text-7xl text-espresso mt-5 leading-[1.02]">Elevate your career, intentionally.</h1>
            <p className="text-lg text-espresso/75 mt-6 max-w-xl leading-relaxed">
              Personalized, boutique career services for professionals ready to move forward with confidence —
              from resumes and LinkedIn to interview coaching and strategy.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="border-l border-champagne/50 pl-8 py-4">
              <span className="font-serif italic leading-[1.05] text-espresso block" style={{ fontSize: "clamp(1.75rem,4vw,3rem)" }}>people, redefined.</span>
              <span className="mt-4 block text-xs tracking-[0.35em] uppercase text-espresso/60">the career atelier</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Service pricing cards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <div data-testid={`career-service-${p.id}`} className={`rounded-2xl p-8 h-full flex flex-col border ${p.featured ? "border-champagne" : "border-champagne/25"}`}>
                <div className="flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-[0.2em] text-champagne">{p.category}</span>
                    {p.featured && <span className="text-xs border border-champagne text-espresso rounded-full px-3 py-1">Most popular</span>}
                  </div>
                  <h3 className="font-serif text-2xl text-espresso mt-3">{p.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed flex-1 text-espresso/70">{DETAILS[p.name] || p.description}</p>
                  <ul className="mt-5 space-y-2">
                    {(p.inclusions || []).map((inc) => (
                      <li key={inc} className="flex gap-2 text-sm text-espresso/75">
                        <Check size={16} className="text-champagne mt-0.5 shrink-0" />{inc}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-serif text-3xl text-espresso">{priceLabel(p)}</span>
                  </div>
                  {p.checkout_enabled && p.price_amount ? (
                    <button onClick={() => startCheckout(p.id)} data-testid={`career-buy-${p.id}`}
                      className="mt-5 rounded-full px-6 py-3 font-medium transition-colors bg-espresso text-ivory hover:bg-forest">
                      Purchase
                    </button>
                  ) : (
                    <Link to="/book" data-testid={`career-book-${p.id}`} className="mt-5 text-center rounded-full px-6 py-3 font-medium border border-espresso text-espresso hover:bg-espresso hover:text-ivory transition-colors">
                      Book a Session
                    </Link>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Career intake */}
      <section className="border-t border-espresso/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">Get started</span>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso mt-3">Share your career goals.</h2>
            <p className="text-espresso/70 mt-4">Tell us where you're headed and upload your current resume — we'll take it from there.</p>
          </Reveal>

          {done ? (
            <Reveal>
              <div data-testid="intake-success" className="mt-10 border border-champagne/40 rounded-2xl p-12 text-center">
                <Check className="mx-auto text-champagne" size={40} />
                <h3 className="font-serif text-3xl text-espresso mt-4">You're all set.</h3>
                <p className="text-espresso/70 mt-3">Your intake has been received securely. We'll reach out to get started on your next chapter.</p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} data-testid="intake-form" className="mt-10 grid md:grid-cols-2 gap-5 rounded-2xl p-8 md:p-10 border border-champagne/30">
                <input type="text" value={form.hp} onChange={(e) => set("hp", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">Full name</label>
                  <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} data-testid="intake-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">Email</label>
                  <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="intake-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">Phone (optional)</label>
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="intake-phone" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">Service of interest</label>
                  <select required value={form.service} onChange={(e) => set("service", e.target.value)} className={inputCls} data-testid="intake-service">
                    <option value="">Select…</option>
                    {Object.keys(DETAILS).map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-espresso mb-2">Target role / positions</label>
                  <input value={form.target_role} onChange={(e) => set("target_role", e.target.value)} className={inputCls} placeholder="e.g. Senior Product Manager" data-testid="intake-role" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-espresso mb-2">Career goals</label>
                  <textarea required rows={4} value={form.career_goals} onChange={(e) => set("career_goals", e.target.value)} className={inputCls} data-testid="intake-goals" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-espresso mb-2">Upload resume / documents (private &amp; secure)</label>
                  <label className="flex items-center gap-3 cursor-pointer bg-ivory border border-dashed border-champagne rounded-lg px-4 py-4 hover:border-espresso/40 transition-colors" data-testid="intake-upload-label">
                    {uploading ? <Loader2 className="animate-spin text-espresso" size={20} /> : <Upload className="text-espresso" size={20} />}
                    <span className="text-espresso/70 text-sm">{uploading ? "Uploading…" : "Click to upload (PDF, DOC, DOCX — max 10MB)"}</span>
                    <input type="file" accept=".pdf,.doc,.docx,.txt,.rtf" onChange={onUpload} className="hidden" data-testid="intake-file-input" />
                  </label>
                  {files.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {files.map((f) => (
                        <div key={f.id} className="flex items-center justify-between border border-champagne/30 rounded-lg px-4 py-2 text-sm text-espresso">
                          <span className="flex items-center gap-2"><FileText size={16} className="text-champagne" />{f.name}</span>
                          <button type="button" onClick={() => removeFile(f.id)} className="text-espresso/50 hover:text-espresso"><X size={16} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <button type="submit" disabled={loading} data-testid="intake-submit" className="inline-flex items-center gap-2 bg-espresso text-ivory rounded-full px-8 py-4 font-medium hover:bg-forest transition-colors disabled:opacity-60">
                    {loading && <Loader2 className="animate-spin" size={18} />} Submit Intake
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
